import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const newItem = () => ({ product_id: null, name: "", description: "", quantity: 1, unit_price: 0, discount_pct: 0 });

export default function QuoteBuilder() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_company: "", notes: "", tax_pct: 13 });
  const [items, setItems] = useState([newItem()]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/products"); setProducts(data || []); } catch {}
  })(); }, []);

  const subtotal = items.reduce((s, it) => s + it.quantity * it.unit_price * (1 - it.discount_pct/100), 0);
  const tax = subtotal * form.tax_pct / 100;
  const total = subtotal + tax;

  const setItem = (i, patch) => setItems((cur) => cur.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.product_id === pid);
    if (!p) return;
    setItem(i, { product_id: p.product_id, name: p.name, description: p.description, unit_price: p.price });
  };

  const submit = async () => {
    if (!form.customer_name) { toast.error("Customer name is required"); return; }
    setLoading(true);
    try {
      await api.post("/quotes", { ...form, items });
      toast.success("Quote created");
      navigate("/quotes");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="quote-builder-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">New quote</div>
        <h1 className="font-display text-4xl font-black tracking-tight mt-1">Build a quote</h1>
      </div>

      <Card className="p-6 rounded-sm border-border grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div><Label>Customer name</Label><Input value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} className="rounded-sm mt-1" data-testid="qb-customer-name" /></div>
        <div><Label>Customer email</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm({...form, customer_email: e.target.value})} className="rounded-sm mt-1" data-testid="qb-customer-email" /></div>
        <div><Label>Company</Label><Input value={form.customer_company} onChange={(e) => setForm({...form, customer_company: e.target.value})} className="rounded-sm mt-1" data-testid="qb-customer-company" /></div>
        <div><Label>Tax %</Label><Input type="number" value={form.tax_pct} onChange={(e) => setForm({...form, tax_pct: parseFloat(e.target.value || 0)})} className="rounded-sm mt-1" /></div>
      </Card>

      <Card className="rounded-sm border-border overflow-x-auto">
        <table className="w-full text-sm dense">
          <thead className="bg-muted/40 text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr><th>Product</th><th>Description</th><th>Qty</th><th>Unit price</th><th>Disc %</th><th className="text-right">Line total</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t border-border" data-testid={`qb-row-${i}`}>
                <td className="min-w-[220px]">
                  <Select value={it.product_id || ""} onValueChange={(v) => pickProduct(i, v)}>
                    <SelectTrigger className="rounded-sm h-9" data-testid={`qb-product-${i}`}><SelectValue placeholder="Pick a product…" /></SelectTrigger>
                    <SelectContent>
                      {products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input className="rounded-sm h-8 mt-1 text-xs" value={it.name} onChange={(e) => setItem(i, { name: e.target.value })} placeholder="Item name" />
                </td>
                <td><Input className="rounded-sm h-9" value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} /></td>
                <td className="w-24"><Input type="number" className="rounded-sm h-9" value={it.quantity} onChange={(e) => setItem(i, { quantity: parseInt(e.target.value || 0) })} /></td>
                <td className="w-32"><Input type="number" step="0.01" className="rounded-sm h-9" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: parseFloat(e.target.value || 0) })} /></td>
                <td className="w-24"><Input type="number" className="rounded-sm h-9" value={it.discount_pct} onChange={(e) => setItem(i, { discount_pct: parseFloat(e.target.value || 0) })} /></td>
                <td className="text-right font-medium">${(it.quantity * it.unit_price * (1 - it.discount_pct/100)).toFixed(2)}</td>
                <td><button className="p-2 text-muted-foreground hover:text-destructive" onClick={() => setItems(items.filter((_, idx) => idx !== i))}><Trash size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-3 border-t border-border">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setItems([...items, newItem()])} data-testid="qb-add-line"><Plus size={14} className="mr-1" /> Add line</Button>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 rounded-sm border-border lg:col-span-2">
          <Label>Notes</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="rounded-sm mt-1 min-h-[120px]" data-testid="qb-notes" />
        </Card>
        <Card className="p-6 rounded-sm border-border space-y-3">
          <div className="font-display font-bold text-lg">Summary</div>
          <div className="text-sm space-y-2">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span data-testid="qb-subtotal">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tax ({form.tax_pct}%)</span><span>${tax.toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-border pt-2 font-display font-bold text-lg"><span>Total</span><span data-testid="qb-total">${total.toFixed(2)} CAD</span></div>
          </div>
          <Button onClick={submit} disabled={loading} className="w-full rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white h-11" data-testid="qb-save">
            {loading ? "Saving…" : "Save quote"}
          </Button>
        </Card>
      </div>
    </div>
  );
}
